import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useAuth, useWS, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, Send, Wifi } from "lucide-react";

const ChatPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const ws = useWS();
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Get navigation source
  const fromProduct = location.state?.from === 'product';
  const productId = location.state?.productId;

  useEffect(() => {
    fetchOtherUser();
    fetchMessages();

    // Poll for new messages (fallback if WebSocket is not connected)
    const interval = setInterval(() => {
      if (!ws?.isConnected) {
        fetchMessages();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [userId, ws?.isConnected]);

  // Handle real-time messages from WebSocket
  useEffect(() => {
    if (ws?.newMessages?.length > 0) {
      const relevantMessages = ws.newMessages.filter(
        msg => msg.sender_id === userId
      );
      
      if (relevantMessages.length > 0) {
        setMessages(prev => [
          ...prev,
          ...relevantMessages.map(msg => ({
            message_id: msg.message_id,
            sender_id: msg.sender_id,
            sender_name: msg.sender_name,
            content: msg.content,
            created_at: msg.created_at
          }))
        ]);
        ws.clearNewMessages();
      }
    }
  }, [ws?.newMessages, userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchOtherUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setOtherUser(response.data);
    } catch (error) {
      toast.error("Usuario no encontrado");
      navigate("/messages");
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/messages`,
        { receiver_id: userId, content: newMessage },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    if (fromProduct && productId) {
      navigate(`/product/${productId}`);
    } else {
      navigate("/messages");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="app-header" data-testid="chat-header">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center gap-4 h-16">
            <button
              onClick={handleBack}
              className="text-[#64748B] hover:text-[#0F172A]"
              data-testid="back-button"
            >
              <ArrowLeft size={24} />
            </button>

            {otherUser && (
              <Link to={`/profile/${otherUser.user_id}`} className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={otherUser.picture} alt={otherUser.name} />
                  <AvatarFallback className="bg-[#475569] text-white">
                    {otherUser.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="font-semibold text-[#0F172A]">{otherUser.name}</h2>
                </div>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-6 pb-24">
        <div className="max-w-lg mx-auto space-y-4" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="text-center text-[#64748B] py-12">
              Envía un mensaje para iniciar la conversación
            </div>
          ) : (
            messages.map((message) => {
              const isSent = message.sender_id === user.user_id;
              return (
                <div
                  key={message.message_id}
                  className={`flex ${isSent ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${message.message_id}`}
                >
                  <div className={isSent ? "message-sent" : "message-received"}>
                    <p>{message.content}</p>
                    <p className={`text-xs mt-1 ${isSent ? "text-white/70" : "text-[#64748B]"}`}>
                      {formatDistanceToNow(new Date(message.created_at), { addSuffix: true, locale: es })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input - Higher z-index to stay above BottomNav */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] p-4 z-50">
        <form onSubmit={handleSend} className="max-w-lg mx-auto flex items-center gap-3">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Escribe un mensaje..."
            className="flex-1 rounded-full border-[#E2E8F0]"
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            className="rounded-full bg-[#475569] hover:bg-[#334155] h-10 w-10"
            disabled={sending || !newMessage.trim()}
            data-testid="send-message"
          >
            <Send size={18} />
          </Button>
        </form>
      </div>

      <BottomNav />
    </div>
  );
};

export default ChatPage;
